import { trpc, queryClient } from '@/utils/trpc';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';
import { toast } from 'sonner';
import { TRPCClientError } from '@trpc/client';
import {
  FindExistingPatientParams,
  SearchPatientsParams,
  SourceType,
} from '../types';
import { mapPatientRequestDetailsToState } from '@/feature/patients/utils/mapPatientRequestDetailsToState';

export const useGetUserEmailsForProfile = (
  organizationProfileId?: string,
  searchTerm?: string,
) => {
  // Only pass searchTerm to API if it's 3+ characters
  // Empty/undefined searchTerm will return all emails (paginated)
  const apiSearchTerm =
    searchTerm && searchTerm.length >= 3 ? searchTerm : undefined;

  const query = useQuery({
    ...trpc.usersRouter.getUserEmailsForProfile.queryOptions({
      organizationProfileId: organizationProfileId || '',
      searchTerm: apiSearchTerm,
    }),
    enabled: !!organizationProfileId,
  });

  // Format emails for UI components (MultiSelect, etc.)
  const emailOptions = useMemo(() => {
    if (!query.data?.data) return [];
    return query.data.data.map((email: string) => ({
      value: email,
      label: email,
    }));
  }, [query.data?.data]);

  return {
    ...query,
    emailOptions, // Ready-to-use formatted options
    emails: query.data?.data, // Raw emails if needed
    totalCount: query.data?.totalCount || 0,
  };
};

export const useFindExistingPatient = (
  params?: FindExistingPatientParams,
  enabled = false,
) => {
  const queryResult = useQuery({
    ...trpc.patientsRouter.getOrganizationPatients.queryOptions(params || {}),
    enabled,
  });

  // to trigger the query if needed
  const execute = (executeParams: FindExistingPatientParams) =>
    queryClient.fetchQuery({
      ...trpc.patientsRouter.getOrganizationPatients.queryOptions(
        executeParams,
      ),
    });

  return { ...queryResult, execute };
};

export const useSearchPatients = (params?: SearchPatientsParams) => {
  return useQuery({
    ...trpc.patientsRouter.searchPatients.queryOptions(
      params || {
        search: {
          terms: [],
        },
        includeRequests: false,
      },
    ),
    enabled: !!params,
  });
};

export const useGetPatientRequest = (
  patientId?: string,
  requestId?: string,
) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientRequestDetails.queryOptions({
      patientId: patientId || '',
      requestId: requestId || '',
    }),
    enabled: !!patientId && !!requestId,
    select: (details) => mapPatientRequestDetailsToState(details),
  });
};

export const useGetPatientsPreviousDocuments = (patientId?: string) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientsPreviousDocuments.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
  });
};

export const useGetPatients = (
  queries?: SearchPatientsParams,
  options?: { enabled?: boolean },
) => {
  const initialQueries: SearchPatientsParams = {
    search: {
      terms: [],
    },
    pagination: {
      offset: 0,
      limit: 10,
    },
    includeRequests: false,
  };

  return useQuery({
    ...trpc.patientsRouter.searchPatients.queryOptions(
      queries || initialQueries,
    ),
    ...options,
  });
};

export const useGetPatientById = (patientId?: string) => {
  return useQuery({
    ...trpc.patientsRouter.getPatient.queryOptions({
      id: patientId || '',
    }),
    enabled: !!patientId,
  });
};

export const useCanCreateStandaloneFacesheetRequest = (patientId?: string) => {
  return useQuery({
    ...trpc.patientsRouter.canCreateStandaloneFacesheetRequest.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
  });
};

export const useCreatePatientWithRequest = () => {
  return useMutation({
    ...trpc.patientsRouter.createPatientWithRequest.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getOrganizationPatients.queryKey(),
      });

      toast.success('Patient created successfully');
    },
    onError: (error) => {
      const message =
        error instanceof TRPCClientError && error.data?.httpStatus === 400
          ? `Failed to create patient: ${error.message}`
          : 'Failed to create patient';
      toast.error(message);
    },
  });
};

export const useCreateRequestForPatient = () => {
  return useMutation({
    ...trpc.patientsRouter.createRequestForPatient.mutationOptions(),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.patientId,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });

      toast.success('Request created successfully');
    },
    onError: (error) => {
      const message =
        error instanceof TRPCClientError && error.data?.httpStatus === 400
          ? `Failed to create request: ${error.message}`
          : 'Failed to create request';
      toast.error(message);
    },
  });
};

export const useEditPatientRequest = () => {
  return useMutation({
    ...trpc.patientsRouter.editPatientRequest.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      // Invalidate all getPatientRequestDetails queries since we don't have patientId
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatientRequestDetails.queryKey(),
      });

      toast.success('Request updated successfully');
    },
    onError: (error) => {
      const message =
        error instanceof TRPCClientError && error.data?.httpStatus === 400
          ? `Failed to update request: ${error.message}`
          : 'Failed to update request';
      toast.error(message);
    },
  });
};

export const useUpdatePatientDemographics = () => {
  return useMutation({
    ...trpc.patientsRouter.editPatient.mutationOptions(),
    onSuccess: (_, variables) => {
      // Invalidate the specific patient query
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.id,
        }),
      });
      // Invalidate the patients list to refresh the table after update
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getOrganizationPatients.queryKey(),
      });
      toast.success('Patient updated successfully.');
    },
    onError: (error) => {
      const message =
        error instanceof TRPCClientError && error.data?.httpStatus === 400
          ? `Failed to update patient: ${error.message}`
          : 'Failed to update patient';
      toast.error(message);
    },
  });
};

export const useChangePatientProfile = () => {
  return useMutation({
    ...trpc.patientsRouter.changePatientProfile.mutationOptions(),
    onSuccess: (_, variables) => {
      // Invalidate the specific patient query
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.patientId,
        }),
      });
      // Invalidate the patients list to refresh the table
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      toast.success('Patient profile changed successfully.');
    },
    onError: (error) => {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to change patient profile.';
      toast.error(errorMessage);
    },
  });
};

export const useGetPatientRequestDetails = (
  patientId?: string,
  requestId?: string,
) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientRequestDetails.queryOptions({
      patientId: patientId || '',
      requestId: requestId || '',
    }),
    enabled: !!patientId && !!requestId,
  });
};

export const useGetStandaloneServiceDetails = (
  patientId?: string,
  requestId?: string,
  requestType?: 'standalone_facesheet' | 'standalone_record_upload',
) => {
  return useQuery({
    ...trpc.patientsRouter.getStandaloneServiceDetails.queryOptions({
      patientId: patientId || '',
      requestId: requestId || '',
      requestType: requestType!,
    }),
    enabled: !!patientId && !!requestId && !!requestType,
  });
};

export const useCancelPatientRequest = () => {
  return useMutation({
    ...trpc.patientsRouter.cancelPatientRequest.mutationOptions(),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.patientId,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getOrganizationPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatientRequestDetails.queryKey({
          patientId: variables.patientId,
          requestId: variables.requestId,
        }),
      });
      toast.success('Request has been cancelled.');
    },
    onError: () => {
      toast.error('Failed to cancel request. Please try again.');
    },
  });
};

export const useCancelPatientOpenRequests = () => {
  return useMutation({
    ...trpc.patientsRouter.cancelPatientOpenRequests.mutationOptions(),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.patientId,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getOrganizationPatients.queryKey(),
      });
      toast.success('All open requests has been cancelled.');
    },
    onError: () => {
      toast.error('Failed to cancel all open requests.');
    },
  });
};

export const useGetPatientFacesheets = (
  params:
    | {
        patientId: string;
        pagination?: { offset: number; limit: number };
      }
    | undefined,
) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientFacesheets.queryOptions(
      params ?? { patientId: '' },
    ),
    enabled: !!params?.patientId,
  });
};

export const useExportPatientLogs = () => {
  return useMutation({
    mutationFn: (input: {
      patientId: string;
      startDate: string;
      endDate: string;
    }) =>
      queryClient.fetchQuery(
        trpc.patientsRouter.exportPatientLogs.queryOptions({
          patientId: input.patientId,
          startDate: input.startDate,
          endDate: input.endDate,
        }),
      ),
    onError: () => {
      toast.error('Failed to export audit logs. Please try again.');
    },
  });
};

export const useGetPatientNonMedicalDocuments = (patientId?: string) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientNonMedicalDocuments.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
  });
};

export const useGetPatientProvidersSummaries = (
  params:
    | {
        patientId: string;
        pagination?: { offset: number; limit: number };
      }
    | undefined,
) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientProvidersSummaries.queryOptions(
      params ?? { patientId: '' },
    ),
    enabled: !!params?.patientId,
    meta: { errorMessage: 'Failed to fetch providers' },
  });
};

export interface PatientProvidersDetailsParams {
  patientId?: string;
  sourceType?: SourceType;
}

export const useGetPatientProvidersDetails = (
  params: PatientProvidersDetailsParams,
) =>
  useQuery({
    ...trpc.patientsRouter.getPatientProvidersDetails.queryOptions({
      patientId: params.patientId || '',
      sourceType: params.sourceType ?? SourceType.UNSPECIFIED,
    }),
    enabled: !!params.patientId && !!params.sourceType,
    meta: { errorMessage: 'Failed to fetch provider details' },
  });

export const useGetPatientRequests = (patientId?: string) => {
  return useQuery({
    ...trpc.patientsRouter.getPatientRequests.queryOptions({
      patientId: patientId || '',
    }),
    enabled: !!patientId,
  });
};

export const useEditProviderFacilityRequest = () => {
  return useMutation({
    ...trpc.patientsRouter.editProviderFacilityRequest.mutationOptions(),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatientRequestDetails.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.searchPatients.queryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: trpc.patientsRouter.getPatient.queryKey({
          id: variables.patientId,
        }),
      });
      toast.success('Provider/Facility updated successfully');
    },
    onError: () => {
      toast.error('Failed to update provider/facility');
    },
  });
};
